import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { num, pct } from "./format";

export const TREND_RANGES = [
  { id: 7, label: "7 Days" },
  { id: 30, label: "30 Days" },
  { id: 90, label: "3 Months" },
  { id: "custom", label: "Custom Range" },
];

const COLORS = { absent: "#dc3545", leave: "#e0a106", pct: "#3a1fd9" };

const TrendTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: "#fff", border: "1px solid #e7e7ef", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
      <b>{p.title}</b>
      <div>Present {num(p.present + p.working)}</div>
      <div>Absent {num(p.absent)}</div>
      <div>Leave {num(p.leave)}</div>
      <div>Attendance {pct(p.attendance_pct)}</div>
    </div>
  );
};

const TrendChart = ({ data, loading, range, onRange, custom, onCustom }) => {
  const [draft, setDraft] = useState(custom);
  const weekly = data?.granularity === "week";
  const points = (data?.points || []).map((p) => ({
    ...p,
    x: weekly ? dayjs(p.label).format("DD MMM") : dayjs(p.label).format("ddd DD"),
    title: weekly
      ? `Week of ${dayjs(p.label).format("DD MMM")} (daily average)`
      : dayjs(p.label).format("DD MMM YYYY"),
  }));

  return (
    <div className={`a2-card ${loading ? "a2-loading" : ""}`}>
      <div className="a2-card-head">
        <div>
          <h3 className="a2-card-title">Attendance Trend</h3>
          <div className="a2-card-sub">
            {weekly ? "Weekly averages" : "Daily"} · {points.length} {weekly ? "weeks" : "days"}
          </div>
        </div>
        <div className="a2-seg a2-noprint">
          {TREND_RANGES.map((r) => (
            <button key={r.id} className={range === r.id ? "active" : ""} onClick={() => onRange(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {range === "custom" && (
        <div className="a2-noprint" style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
          <div className="a2-field">
            <label>From</label>
            <input type="date" className="a2-input" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
          </div>
          <div className="a2-field">
            <label>To</label>
            <input type="date" className="a2-input" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
          </div>
          <button className="a2-btn a2-btn-primary" onClick={() => onCustom(draft)}>Apply</button>
          <span className="a2-card-sub">Up to 92 days; ranges over 31 days show weekly averages.</span>
        </div>
      )}

      <div className="a2-legend">
        <span><i style={{ background: COLORS.pct, height: 3 }} /> Attendance % (right axis)</span>
        <span><i style={{ background: COLORS.leave }} /> Leave</span>
        <span><i style={{ background: COLORS.absent }} /> Absent</span>
      </div>
      <div style={{ height: 280 }}>
        {points.length === 0 && !loading ? (
          <div className="a2-empty">No attendance data for this range.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={points} margin={{ top: 10, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#eeeef4" vertical={false} />
              <XAxis dataKey="x" tick={{ fontSize: 11, fill: "#6b6b80" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="count" tick={{ fontSize: 11, fill: "#6b6b80" }} axisLine={false} tickLine={false} tickFormatter={(v) => num(v)} width={56} />
              <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: COLORS.pct }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} width={40} />
              <Tooltip content={<TrendTooltip />} />
              <Bar yAxisId="count" dataKey="leave" stackId="s" fill={COLORS.leave} maxBarSize={36} />
              <Bar yAxisId="count" dataKey="absent" stackId="s" fill={COLORS.absent} maxBarSize={36} radius={[4, 4, 0, 0]} />
              <Line yAxisId="pct" dataKey="attendance_pct" stroke={COLORS.pct} strokeWidth={2} dot={{ r: 3, fill: "#fff" }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default TrendChart;
